/**
 * Feb 2026 · Concierge-Übergabe-Polling.
 *
 * User-Directive (1 Sep 2026) „Mach die Neuzuweisung einer Buchung durch
 * den Admin einfacher, mit sichtbarem Übergabe-Toast für den neuen
 * Verantwortlichen." Für jeden ungelesenen `team_handover`-Record
 * (Backend legt ihn beim Admin-Reassign an) zeigen wir einen persistenten
 * Sonner-Toast. Klick auf „Ansehen" → springt in die Buchungsliste mit
 * `?open={booking_id}` (BookingDetailModal öffnet automatisch). Wischen
 * / Ausblenden → `POST /team/handovers/{id}/dismiss`, damit derselbe
 * Toast nicht bei der nächsten Poll-Runde wieder aufpoppt.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import api from "./api";
import { useAuth } from "./auth";
import { deDate } from "./dateFormat";

const POLL_MS = 30000;

/**
 * Poll-Hook für Concierge-/Admin-Übergaben. Nur aktiv für Team-User; für
 * Kunden ist es ein No-Op (keine API-Calls, keine Timer).
 * „Gesehen"-Marker liegen serverseitig pro Nutzer (users.ui_prefs.seen_handovers).
 *
 * @param {{ enabled: boolean, onOpenBooking: (bookingId: string) => void }} opts
 */
export function useHandoverPolling({ enabled, onOpenBooking }) {
  const { uiPrefs, setUiPrefs } = useAuth();
  const seenRef = useRef(null);
  if (seenRef.current === null) {
    seenRef.current = new Set(Array.isArray(uiPrefs?.seen_handovers) ? uiPrefs.seen_handovers : []);
  }
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const dismiss = async (id) => {
      try {
        await api.post(`/team/handovers/${id}/dismiss`);
      } catch (e) {
        /* silent — nächster Poll überspringt ihn nur, wenn read_at gesetzt ist */
      }
    };

    const showToast = (h) => {
      const parts = [];
      if (h.customer_name) parts.push(h.customer_name);
      if (h.property_name) parts.push(h.property_name);
      const period = h.booking?.from_date && h.booking?.to_date
        ? `${deDate(h.booking.from_date)} → ${deDate(h.booking.to_date)}`
        : "";
      if (period) parts.push(period);
      const description = parts.join(" · ") || "Neue Buchung";
      const admin = h.admin_name || "Admin";
      toast.info(`Buchung übergeben — von ${admin}`, {
        description,
        duration: Infinity,
        id: `handover-${h.id}`,
        action: h.booking?.id
          ? {
              label: "Ansehen",
              onClick: () => {
                dismiss(h.id);
                onOpenBooking?.(h.booking.id);
              },
            }
          : undefined,
        onDismiss: () => dismiss(h.id),
        onAutoClose: () => dismiss(h.id),
      });
    };

    const load = async () => {
      try {
        const { data } = await api.get("/team/handovers/mine");
        if (cancelled || !Array.isArray(data)) return;
        let added = false;
        for (const h of data) {
          if (!h?.id || seenRef.current.has(h.id)) continue;
          seenRef.current.add(h.id);
          added = true;
          showToast(h);
        }
        if (added) setUiPrefs({ seen_handovers: Array.from(seenRef.current).slice(-300) });
      } catch {
        /* silent — auth-Erneuerung oder Netz-Delle */
      }
    };

    load();
    timerRef.current = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [enabled, onOpenBooking, setUiPrefs]);
}
